import { useEffect, useState } from "react";
import { CreditCard, UsersThree, ArrowUp, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Switch } from "./ui/switch";
import { UpgradeDialog } from "./UpgradeDialog";
import { BUSINESS_TYPES } from "./PlanGate";

const TIER_COLORS = { bronze: "#B87333", gold: "#FFD700", silver: "#C0C0C0" };

export const SubscriptionCard = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = () => api.get("/platform/billing/status").then(({ data }) => setStatus(data)).catch(() => {});

  useEffect(() => {
    load().then?.(() => {});
    if (new URLSearchParams(window.location.search).get("upgrade") === "1") {
      setUpgradeOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!status || !status.required) return null;

  const manage = async () => {
    try {
      const { data } = await api.post("/platform/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
    }
  };

  const color = TIER_COLORS[status.plan] || "#2E5BA8";
  const renews = status.current_period_end ? new Date(status.current_period_end * 1000).toLocaleDateString() : null;
  const nearCap = status.client_limit != null && status.client_count >= status.client_limit;

  return (
    <section className="card-flat p-6" data-testid="subscription-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CreditCard size={20} weight="duotone" className="text-brand-light" />
          <div>
            <h2 className="font-display text-xl">Your Traina plan</h2>
            <p className="tracker text-[10px]" style={{ color }}>
              {status.plan ? `${status.plan.toUpperCase()} · ${status.status?.toUpperCase()}` : "NO PLAN YET"}
              {renews ? ` · renews ${renews}` : ""}
            </p>
            {status.client_limit != null && (
              <p className={`tracker text-[10px] mt-1 ${nearCap ? "text-amber-400" : "text-zinc-500"}`} data-testid="subscription-client-usage">
                {status.client_count} / {status.client_limit} clients used{nearCap ? " — upgrade to add more" : ""}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {status.active && (
            <button onClick={() => setUpgradeOpen(true)} className="btn-primary !py-2 !px-4 !text-xs" data-testid="subscription-upgrade-btn">
              <ArrowUp size={12} weight="bold" className="inline mr-1 -mt-0.5" />Upgrade plan
            </button>
          )}
          <button onClick={manage} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="subscription-manage-btn">
            Manage billing
          </button>
        </div>
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} status={status} onUpgraded={load} />
    </section>
  );
};

export const BusinessTypeCard = () => {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(null);
  if (!user || user.role !== "trainer") return null;

  const set = async (id) => {
    if (id === user.business_type) return;
    setSaving(id);
    try {
      await api.post("/platform/billing/business-type", { business_type: id });
      await refresh();
      toast.success(id === "group" ? "Set up as a group sessions business" : "Set up as a personal training business");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally { setSaving(null); }
  };

  return (
    <section className="card-flat p-6" data-testid="business-type-card">
      <div className="flex items-center gap-3 mb-4">
        <Storefront size={20} weight="duotone" className="text-brand-light" />
        <div>
          <h2 className="font-display text-xl">Business type</h2>
          <p className="tracker text-[10px] text-zinc-500">Shapes your plan options and which tab your portal opens on</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {BUSINESS_TYPES.map((b) => {
          const Icon = b.icon;
          const on = user.business_type === b.id;
          return (
            <button key={b.id} onClick={() => set(b.id)} disabled={saving !== null}
              className={`border p-4 text-left flex items-start gap-3 transition-colors ${on ? "border-brand bg-brand/5" : "border-white/10 hover:border-white/30"}`}
              data-testid={`business-type-option-${b.id}`}>
              <Icon size={20} weight="duotone" className={on ? "text-brand" : "text-zinc-500"} />
              <div>
                <div className="font-display text-lg">{b.title}</div>
                <p className="text-zinc-500 text-xs">{b.blurb}</p>
                {on && <span className="tracker text-[9px] text-brand-light mt-1 inline-block">CURRENT</span>}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export const CoachPlansCard = () => {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState(null);

  useEffect(() => {
    if (!user?.is_owner) return;
    api.get("/platform/billing/coaches").then(({ data }) => setCoaches(data)).catch(() => {});
  }, [user?.is_owner]);

  if (!user?.is_owner || !coaches) return null;
  const others = coaches.filter((c) => !c.is_owner);

  const toggleComp = async (c) => {
    try {
      const { data } = await api.post(`/platform/billing/comp/${c.id}`, { comp: !c.comp });
      setCoaches((list) => list.map((x) => (x.id === c.id ? { ...x, comp: data.comp } : x)));
      toast.success(data.comp ? `${c.name} now has free access` : `${c.name} must subscribe`);
    } catch (_e) {
      toast.error("Could not update");
    }
  };

  return (
    <section className="card-flat p-6" data-testid="coach-plans-section">
      <div className="flex items-center gap-3 mb-4">
        <UsersThree size={20} weight="duotone" className="text-brand-light" />
        <div>
          <h2 className="font-display text-xl">Coach subscriptions</h2>
          <p className="tracker text-zinc-500 text-[10px]">Who's on which plan — flip the switch to gift free access</p>
        </div>
      </div>
      {others.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="coach-plans-empty">No other coaches yet — when coaches register, they'll appear here.</p>
      ) : (
        <div className="space-y-2" data-testid="coach-plans-list">
          {others.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 border border-white/10 bg-zinc-900/40 px-4 py-3" data-testid={`coach-plan-row-${c.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-zinc-200 truncate">{c.name}</div>
                <div className="tracker text-[9px] text-zinc-500">{c.email}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`tracker text-[9px] px-2 py-0.5 border ${c.comp ? "border-emerald-500/40 text-emerald-400" : c.status === "active" ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
                  {c.comp ? "FREE (GIFTED)" : c.plan ? `${c.plan.toUpperCase()} · ${(c.status || "").toUpperCase()}` : ["active", "trialing"].includes(c.group) ? "GROUP ONLY" : "NOT SUBSCRIBED"}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="tracker text-[9px] text-zinc-500">Free</span>
                  <Switch checked={c.comp} onCheckedChange={() => toggleComp(c)} data-testid={`coach-comp-toggle-${c.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
